import { Autocomplete, Fieldset, Grid, Input, TextInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';
import { IMaskInput as Mask } from 'react-imask';
import type { UseQueryResult } from 'react-query';
import type { ApiResponse } from '../../../util/types/Response';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';

type UpsertBranchFormProps<T> = {
	pharmaciesQuery: UseQueryResult<ApiResponse<SelectableBranchDto>, unknown>;
	form: UseFormReturnType<T, (values: T) => T>;
	inputs: T;
	area: string;
};

export function UpsertBranchForm<T>({
	pharmaciesQuery,
	inputs,
	form,
	area,
}: UpsertBranchFormProps<T>) {
	const [areaOptions, setAreaOptions] = useState<undefined | string[]>([]);

	useEffect(() => {
		setAreaOptions(pharmaciesQuery.data?.value.areaNames);
	}, [pharmaciesQuery.data]);

	const newAreaMessage = useMemo(() => {
		if (
			!pharmaciesQuery.data?.value.areaNames.includes(area) &&
			area.length >= 1
		) {
			return "A new area will be created if the input doesn't match existing records";
		}

		return undefined;
	}, [inputs]);

	return (
		<>
			<Fieldset>
				<Grid>
					<Grid.Col span={6} pb={0}>
						<TextInput
							data-cy={'branchName-input'}
							label={'Branch name'}
							placeholder="Branch name..."
							key={form.key('name')}
							{...form.getInputProps('name')}
						/>
					</Grid.Col>
					<Grid.Col span={6} pb={0}>
						<Autocomplete
							data-cy={'area-input'}
							label={'Area'}
							placeholder="Select area..."
							description={newAreaMessage}
							data={areaOptions}
							key={form.key('area')}
							{...form.getInputProps('area')}
						/>
					</Grid.Col>
					<Grid.Col span={4} pb={0}>
						<TextInput
							data-cy={'email-input'}
							label={'Email'}
							placeholder="Insert email..."
							key={form.key('email')}
							{...form.getInputProps('email')}
						/>
					</Grid.Col>
					<Grid.Col span={4} pb={0}>
						<Input.Wrapper
							label={'Phone Number'}
							key={form.key('phone')}
							{...form.getInputProps('phone')}
						>
							<Input
								data-cy={'phone-input'}
								component={Mask}
								mask={'00000 000000'}
								placeholder={'Insert phone number...'}
								key={form.key('phone')}
								{...form.getInputProps('phone')}
							/>
						</Input.Wrapper>
					</Grid.Col>
					<Grid.Col span={4}>
						<TextInput
							data-cy={'website-input'}
							label={'Website'}
							placeholder="Insert website..."
							key={form.key('website')}
							{...form.getInputProps('website')}
						/>
					</Grid.Col>
					<Grid.Col span={6} pb={0}>
						<TextInput
							data-cy={'addressLineOne-input'}
							label="Address Line 1"
							placeholder="Insert address..."
							key={form.key('addressLineOne')}
							{...form.getInputProps('addressLineOne')}
						/>
					</Grid.Col>
					<Grid.Col span={6} pb={0}>
						<TextInput
							data-cy={'addressLineTwo-input'}
							label="Address Line 2"
							placeholder="Insert address..."
							key={form.key('addressLineTwo')}
							{...form.getInputProps('addressLineTwo')}
						/>
					</Grid.Col>
					<Grid.Col span={4} pb={0}>
						<TextInput
							data-cy={'town-input'}
							label={'Town'}
							placeholder="Insert town..."
							key={form.key('town')}
							{...form.getInputProps('town')}
						/>
					</Grid.Col>
					<Grid.Col span={4} pb={0}>
						<TextInput
							data-cy={'postCode-input'}
							label={'Post Code'}
							placeholder="Insert post code..."
							key={form.key('postCode')}
							{...form.getInputProps('postCode')}
						/>
					</Grid.Col>
					<Grid.Col span={4} pb={0}>
						<TextInput
							data-cy={'county-input'}
							label={'County'}
							placeholder="Insert county..."
							key={form.key('county')}
							{...form.getInputProps('county')}
						/>
					</Grid.Col>
				</Grid>
			</Fieldset>
		</>
	);
}
